import type { RawRelationship, RelationshipRowVM } from './relationships.types';

interface ResolvableProjection {
  tenantId: string;
  partyId?: string | null;
  displayName?: string | null;
  persona?: string | null;
  country?: string | null;
  countryCode?: string | null;
}

/** Shown when an org's name can't be resolved — never a raw UUID (T08 §2). */
export const UNRESOLVED_ORG_LABEL = 'Unnamed organisation';

/**
 * Resolve a human label for an org identity projection.
 *
 * `displayName` resolves best-effort via a cross-service name lookup and is
 * often `null` — for an unindexed org or a transient name-service degradation
 * (the whole batch → null). Per T08 §2 we MUST NOT fall back to `partyId`/
 * `tenantId` (both UUIDs in prod) as a primary label — show a graceful
 * placeholder instead. Logic keys off the ids on the VM, never this label.
 */
export function resolveDisplayName(p: ResolvableProjection): string {
  return p.displayName?.trim() || UNRESOLVED_ORG_LABEL;
}

/**
 * Collapse a raw `myRelationships` row into a direction-aware view model.
 *
 * - SOURCE → counterparty is `target`; `services` + `canRunAs` populated.
 * - TARGET → counterparty is `source`; `services` empty, `readOnly` true.
 */
export function mapRelationship(r: RawRelationship): RelationshipRowVM {
  const isSource = r.direction === 'SOURCE';
  const projection = isSource ? r.target : r.source;

  return {
    id: r.id,
    kind: r.kind,
    direction: r.direction,
    counterparty: {
      tenantId: projection?.tenantId ?? '',
      partyId: projection?.partyId ?? null,
      displayName: projection ? resolveDisplayName(projection) : '—',
      persona: projection?.persona ?? null,
      country: projection?.country ?? null,
      countryCode: projection?.countryCode ?? null,
    },
    services: isSource ? (r.service ?? []) : [],
    canRunAs: r.canRunAs,
    readOnly: !r.canRunAs,
  };
}
